import React, { useMemo, useState, useEffect, useRef } from 'react';
import { IonButton, IonCard, IonCardContent, IonIcon } from '@ionic/react';
import {
  bodyOutline,
  chevronForwardOutline,
  timeOutline,
} from 'ionicons/icons';
import { useAuth } from '../../../auth';
import { getDoc, DocumentReference, Timestamp } from 'firebase/firestore';
import { machineSessionRepository, workoutRepository } from '../../../repositories/LocalRepositories';
import { useIonViewWillEnter } from '@ionic/react';
import {
  convertFirestoreTimestampToDate,
  calculateDuration,
} from '../../../utils/TrainingSessionUtils';

import './TrainingSessions.css';

type Variant = 'page' | 'preview';

type Props = {
  variant?: Variant;
  previewVisibleCount?: number; // preview shows only this many, no blur
};

const PAGE_SIZE = 5;

// Minimal local types that match your datamodel
type MachineExercise = { name: string; muscles: string[] };
type MachineDoc = {
  id: string;
  title?: string;
  qrcode?: string;
  image?: { downloadURL: string }[];
  exercises?: MachineExercise[];
};

type MachineSessionDoc = {
  id: string;
  training_session_id?: string;
  machine_ref?: DocumentReference;
  exercise_name?: string;
  date_used?: Timestamp;
  sets?: any[];
  user_id?: string;
};

const TrainingSessionsWidget: React.FC<Props> = ({
  variant = 'page',
  previewVisibleCount = 3,
}) => {
  const { userId } = useAuth();
  const mountedRef = useRef(true);
  const initialLoadStartedRef = useRef(false);

  const [trainingSessions, setTrainingSessions] = useState<any[]>([]);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);

  const [musclesBySessionId, setMusclesBySessionId] = useState<
    Record<string, string[]>
  >({});

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchTrainingSessions = async (loadMore = false) => {
    if (!userId) {
      console.error('User is not authenticated.');
      return;
    }

    try {
      const isPreview = variant === 'preview';

      const allSessions = await workoutRepository.list(userId);
      if (isPreview) {
        if (mountedRef.current) setTrainingSessions(allSessions.slice(0, previewVisibleCount));
        return;
      }
      const offset = loadMore ? Number(lastVisible || 0) : 0;
      const newSessions = allSessions.slice(offset, offset + PAGE_SIZE);

      if (!mountedRef.current) return;

      setTrainingSessions(prev =>
        loadMore ? [...prev, ...newSessions] : newSessions
      );

      setLastVisible(offset + newSessions.length);
      setHasMore(offset + newSessions.length < allSessions.length);
    } catch (error) {
      console.error('Error fetching training sessions:', error);
    }
  };

  useIonViewWillEnter(() => {
    initialLoadStartedRef.current = true;
    setLastVisible(null);
    setHasMore(true);
    setMusclesBySessionId({});
    fetchTrainingSessions(false);
  });

  // HighscoreContent loads its Firebase data before mounting this widget.
  // On the Statistics screen that means Ionic's first view-enter event has
  // already fired, so fetch once on mount when no view-enter load started.
  useEffect(() => {
    if (!userId || initialLoadStartedRef.current) return;

    initialLoadStartedRef.current = true;
    setLastVisible(null);
    setHasMore(true);
    setMusclesBySessionId({});
    fetchTrainingSessions(false);
    // fetchTrainingSessions intentionally uses the current widget props/state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, variant, previewVisibleCount]);

  useEffect(() => {
    if (!userId) return;
    if (!trainingSessions || trainingSessions.length === 0) return;

    let cancelled = false;

    const compute = async () => {
      try {
        const machineCache = new Map<string, MachineDoc | null>();
        const updates: Record<string, string[]> = {};

        for (const session of trainingSessions) {
          const sessionId: string | undefined = session?.id;
          if (!sessionId) continue;

          const machineSessionIds: string[] = Array.isArray(
            session?.machine_sessions
          )
            ? session.machine_sessions
            : [];

          if (machineSessionIds.length === 0) {
            updates[sessionId] = [];
            continue;
          }

          const msDocs: (MachineSessionDoc | null)[] = await Promise.all(
            machineSessionIds.map(
              async (msId): Promise<MachineSessionDoc | null> => {
                try {
                  return await machineSessionRepository.get(msId);
                } catch {
                  return null;
                }
              }
            )
          );

          const musclesSet = new Set<string>();

          for (const ms of msDocs) {
            if (!ms) continue;

            // Optional user guard
            if (ms.user_id && ms.user_id !== userId) continue;

            const machineRef = ms.machine_ref;
            const exerciseName = ms.exercise_name;

            if (!machineRef) continue;

            const machineKey = machineRef.path;

            let machineDoc = machineCache.get(machineKey) ?? null;

            if (!machineCache.has(machineKey)) {
              try {
                const machineSnap = await getDoc(machineRef);
                if (machineSnap.exists()) {
                  const mdata = machineSnap.data() as Omit<MachineDoc, 'id'>;
                  machineDoc = { id: machineSnap.id, ...mdata };
                } else {
                  machineDoc = null;
                }
              } catch {
                machineDoc = null;
              }
              machineCache.set(machineKey, machineDoc);
            }

            if (!machineDoc) continue;

            const exercises = Array.isArray(machineDoc.exercises)
              ? machineDoc.exercises
              : [];

            if (exerciseName) {
              const normalizedExerciseName = exerciseName.trim().toLocaleLowerCase();
              const ex = exercises.find(e => e?.name?.trim().toLocaleLowerCase() === normalizedExerciseName);
              const muscles = Array.isArray(ex?.muscles) ? ex!.muscles : [];
              muscles.forEach(m => {
                const t = typeof m === 'string' ? m.trim() : '';
                if (t) musclesSet.add(t);
              });
            } else {
              exercises.forEach(e => {
                const muscles = Array.isArray(e?.muscles) ? e.muscles : [];
                muscles.forEach(m => {
                  const t = typeof m === 'string' ? m.trim() : '';
                  if (t) musclesSet.add(t);
                });
              });
            }
          }

          updates[sessionId] = Array.from(musclesSet);
        }

        if (!cancelled) {
          setMusclesBySessionId(prev => ({
            ...prev,
            ...updates,
          }));
        }
      } catch (e) {
        console.error('Error computing muscles trained:', e);
      }
    };

    compute();

    return () => {
      cancelled = true;
    };
  }, [trainingSessions, userId]);

  const musclesTextFor = (sessionId: string) => {
    const arr = musclesBySessionId[sessionId] || [];
    return arr.length ? arr.join(', ') : '-';
  };

  const groupedSessions = useMemo(() => {
    return trainingSessions.reduce(
      (groups, session) => {
        const dateObj = convertFirestoreTimestampToDate(session.start_date);
        const dateKey = dateObj
          ? dateObj.toLocaleDateString('de-DE')
          : 'Unbekanntes Datum';
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(session);
        return groups;
      },
      {} as Record<string, any[]>
    );
  }, [trainingSessions]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedSessions).sort((a, b) => {
      const [dayA, monA, yrA] = a.split('.');
      const [dayB, monB, yrB] = b.split('.');
      const dateA = new Date(`${yrA}-${monA}-${dayA}`);
      const dateB = new Date(`${yrB}-${monB}-${dayB}`);
      return dateB.getTime() - dateA.getTime();
    });
  }, [groupedSessions]);

  const flattenedSessions = useMemo(() => {
    const out: any[] = [];
    sortedDates.forEach(dateKey => {
      (groupedSessions[dateKey] || []).forEach(session => {
        out.push({ ...session, __dateKey: dateKey });
      });
    });
    return out;
  }, [sortedDates, groupedSessions]);

  if (variant === 'preview') {
    const visible = flattenedSessions.slice(0, previewVisibleCount);

    return (
      <div className="tsw">
        <div className="tsw__headingBlock">
          <span>Workout history</span>
          <h2 className="tsw__heading">Last training sessions</h2>
        </div>

        <div className="tsw__cards">
          {visible.map(session => {
            const startDate = convertFirestoreTimestampToDate(
              session.start_date
            );
            const endDate = convertFirestoreTimestampToDate(session.end_date);

            const duration =
              startDate && endDate
                ? calculateDuration(startDate, endDate)
                : 'Duration not available';

            return (
              <IonCard
                key={session.id}
                className="welcome-card tsw__card"
                button
                routerLink={`/my/sessions/${session.id}`}
              >
                <IonCardContent className="tsw__cardContent">
                  <div className="tsw__cardDate">
                    {' '}
                    <strong>{session.__dateKey}</strong>
                  </div>

                  <div className="tsw__line">
                    <strong>Muscles trained:</strong>{' '}
                    {musclesTextFor(session.id)}
                  </div>

                  <div className="tsw__line">
                    <strong>Duration:</strong> {duration}
                  </div>
                </IonCardContent>
              </IonCard>
            );
          })}
        </div>

        <IonButton
          expand="block"
          color="primary"
          routerLink="/my/sessions/"
          className="tsw__showMoreBtn"
        >
          Show more
        </IonButton>
      </div>
    );
  }

  return (
    <div className="sessions-history">
      {sortedDates.length > 0 ? (
        <div className="sessions-history__groups">
          {sortedDates.map(dateKey => (
            <section className="sessions-history__group" key={dateKey}>
              <h2>{dateKey}</h2>

              <div className="sessions-history__cards">
                {(groupedSessions[dateKey] || []).map(session => {
                  const startDate = convertFirestoreTimestampToDate(
                    session.start_date
                  );
                  const endDate = convertFirestoreTimestampToDate(
                    session.end_date
                  );

                  const formattedStartTime = startDate
                    ? startDate.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'Time unavailable';

                  const duration =
                    startDate && endDate
                      ? calculateDuration(startDate, endDate)
                      : 'Duration not available';

                  return (
                    <IonCard
                      key={session.id}
                      className="sessions-history__card"
                      button
                      routerLink={`/my/sessions/${session.id}`}
                    >
                      <IonCardContent>
                        <div className="sessions-history__time">
                          <span>Workout</span>
                          <strong>{formattedStartTime}</strong>
                        </div>
                        <div className="sessions-history__details">
                          <div>
                            <IonIcon icon={timeOutline} aria-hidden="true" />
                            <span>{duration}</span>
                          </div>
                          <div>
                            <IonIcon icon={bodyOutline} aria-hidden="true" />
                            <span>{musclesTextFor(session.id)}</span>
                          </div>
                        </div>
                        <IonIcon
                          icon={chevronForwardOutline}
                          className="sessions-history__arrow"
                          aria-hidden="true"
                        />
                      </IonCardContent>
                    </IonCard>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="sessions-history__empty">
          Your completed workouts will appear here.
        </div>
      )}

      {hasMore && (
        <IonButton
          expand="block"
          className="sessions-history__load-more"
          onClick={() => fetchTrainingSessions(true)}
        >
          Load More
        </IonButton>
      )}
    </div>
  );
};

export default TrainingSessionsWidget;
